import { useEffect, useState } from 'react';
import Icon from './Icon.jsx';
import { Channels } from './FanOwlChannels.jsx';

// ─── Conversation themes — what hundreds of Fan Owl chats are about ──────────
// One panel, two homes: Fan Owl → Reports (above Conversations, following the
// report's period + channel) and the Conversations page (its own period chips).
// The server (server/fanOwlThemes.js) tags each chat once in the background, so
// reading this never waits on a model; only "Summarise" and "Tag now" do.
// Flag fanowl.themes: the client route is gated, so a 403 simply hides the panel.
// Mobile-first: one column, every row a ≥44px tap target, bars not tables.

const small = { fontSize: 11.5, color: 'var(--muted)', margin: '2px 0 4px' };
const btn = { padding: '8px 14px', borderRadius: 8, border: '1.5px solid var(--hairline)', background: 'transparent', color: 'var(--text)', fontSize: 12.5, cursor: 'pointer', minHeight: 40 };
const chip = (on) => ({ minHeight: 40, padding: '6px 14px', borderRadius: 999, fontSize: 13, cursor: 'pointer', border: '1px solid var(--hairline)', background: on ? 'var(--text, #111)' : 'transparent', color: on ? 'var(--bg, #fff)' : 'inherit', fontWeight: on ? 700 : 500 });
const H = ({ children }) => <h3 style={{ fontSize: 14, fontWeight: 700, margin: '18px 0 2px' }}>{children}</h3>;

const PERIODS = [['7d', 'Last 7 days'], ['30d', 'Last 30 days'], ['all', 'All time']];
const periodQuery = (p) => (p === 'all' ? 'range=all' : `days=${parseInt(p, 10) || 30}`);
const ago = (iso) => {
  const m = Math.round((Date.now() - Date.parse(iso)) / 60000);
  if (!Number.isFinite(m)) return '';
  return m < 60 ? `${Math.max(1, m)} min ago` : m < 2880 ? `${Math.round(m / 60)} h ago` : `${Math.round(m / 1440)} days ago`;
};
const ANSWER_LABEL = { no: 'not answered', partly: 'partly answered', yes: 'answered' };

// `query` + `period` given → the panel follows the host's window (Reports);
// omitted → it carries its own period chips (Conversations page).
// `insightsBase` (the Conversations page) → the per-channel cards (unique fans,
// chats, messages — the Fan Owl report's own numbers) sit above At a glance for
// the same period. The report passes none: it draws the cards itself, as filters.
export default function ChatThemes({ base, trBase = '', query, period, channel = '', title = 'What fans are asking', insightsBase = '' }) {
  const [ownPeriod, setOwnPeriod] = useState('30d');
  const p = period || ownPeriod;
  const q = query || periodQuery(p);
  const [data, setData] = useState(null);
  const [hidden, setHidden] = useState(false);
  const [busy, setBusy] = useState('');
  const [note, setNote] = useState(null); // { text, bad }
  const [open, setOpen] = useState('');
  const [showAllThemes, setShowAllThemes] = useState(false);
  const [showAllGaps, setShowAllGaps] = useState(false);
  const qs = `${q}&p=${encodeURIComponent(p)}${channel ? `&channel=${channel}` : ''}`;
  const bodyQuery = () => Object.fromEntries(new URLSearchParams(qs));

  const [channels, setChannels] = useState(null); // { web, whatsapp, total } — a 403 (no manager role / flag off) simply shows none
  useEffect(() => {
    if (!insightsBase) { setChannels(null); return undefined; }
    let on = true;
    setChannels(null);
    fetch(`${insightsBase}?${q}`).then((r) => (r.ok ? r.json() : null)).then((d) => { if (on) setChannels(d?.channels || null); }).catch(() => { if (on) setChannels(null); });
    return () => { on = false; };
  }, [insightsBase, q]);
  useEffect(() => {
    let on = true;
    setData(null); setOpen(''); setNote(null);
    fetch(`${base}?${qs}`)
      .then((r) => { if (r.status === 403) { if (on) setHidden(true); return null; } return r.json(); })
      .then((d) => { if (on && d) { setHidden(false); setData(d); } })
      .catch(() => { if (on) setData(null); });
    return () => { on = false; };
  }, [base, qs]);

  async function post(path, label) {
    setBusy(label); setNote(null);
    try {
      const r = await fetch(`${base}/${path}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(bodyQuery()) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'That didn’t work — try again.');
      if (path === 'summary') setData((x) => ({ ...x, summary: d.summary }));
      else { setData(d); setNote({ text: d.left ? `Read ${d.tagged} — ${d.left} still to go, the rest follow in the background.` : `Read ${d.tagged} new chat${d.tagged === 1 ? '' : 's'}.` }); }
    } catch (e) { setNote({ text: e.message, bad: true }); }
    setBusy('');
  }

  if (hidden) return null;
  const t = data?.totals;
  const themes = data?.themes || [];
  const max = Math.max(1, ...themes.map((x) => x.c));
  const pct = (n) => (t?.typed ? `${Math.round((n / t.typed) * 100)}%` : '—');
  const shownThemes = showAllThemes ? themes : themes.slice(0, 6);
  const shownGaps = showAllGaps ? (data?.unanswered || []) : (data?.unanswered || []).slice(0, 5);
  return (
    <div id="fanowl-themes" style={{ marginTop: 14 }}>
      <H>{title}</H>
      {!period && (
        <div role="tablist" aria-label="Themes period" style={{ display: 'flex', flexWrap: 'wrap', gap: 6, margin: '6px 0 8px' }}>
          {PERIODS.map(([k, label]) => <button key={k} type="button" role="tab" aria-selected={p === k} onClick={() => setOwnPeriod(k)} style={chip(p === k)}>{label}</button>)}
        </div>
      )}
      {channels && (
        <Section title="Conversations by channel">
          <Channels channels={channels} />
        </Section>
      )}
      {!data && <p style={small}>Reading the themes…</p>}
      {data && data.flagOn === false && <p style={{ ...small, color: 'var(--warn, #b45309)' }}>Staff view only — conversation themes are switched off for this client, so new chats aren’t being tagged (Admin → Product → Flags → Fan Owl → Conversation themes).</p>}
      {data && data.flagOn && !data.aiReady && <p style={{ ...small, color: 'var(--warn, #b45309)' }}>Themes need AI — set an Anthropic key in Integrations and chats start being tagged within the hour.</p>}

      {data && (
        <div style={{ display: 'grid', gap: 10, marginTop: 6 }}>
          {/* At a glance — the same stat tiles as the rest of the Fan Owl report. */}
          <Section title="At a glance">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(104px, 1fr))', gap: 8 }}>
              <Stat label="Questions asked" value={t.typed} delta={t.prevTyped == null ? null : t.typed - t.prevTyped} />
              <Stat label="Answered" value={pct(t.answered)} hint={`${t.answered} chats`} />
              <Stat label="Not answered" value={t.unanswered} tone={t.unanswered ? 'warn' : ''} hint={t.partly ? `+${t.partly} partly` : ''} />
              <Stat label="Ready to buy" value={data.intent.buying} hint={pct(data.intent.buying)} />
              <Stat label="Had a problem" value={data.intent.support} hint={pct(data.intent.support)} />
              <Stat label="Frustrated" value={data.sentiment.frustrated} tone={data.sentiment.frustrated ? 'warn' : ''} />
              <Stat label="Starter taps only" value={t.starterOnly} hint="not counted" />
            </div>
            <p style={{ ...small, marginTop: 8 }}>
              Questions asked are chats where the fan typed something; a chat that was only a tap on a starter button is counted apart.
              {data.pending ? ` ${data.pending} newer chat${data.pending === 1 ? ' is' : 's are'} still to be read.` : ''}
            </p>
            {(data.pending > 0 && data.flagOn && data.aiReady) && (
              <button type="button" style={btn} disabled={!!busy} onClick={() => post('run', 'run')}>{busy === 'run' ? 'Reading chats…' : `Read the ${data.pending} new chat${data.pending === 1 ? '' : 's'} now`}</button>
            )}
            {note && <p role="status" style={{ ...small, color: note.bad ? 'var(--danger, #b3261e)' : 'var(--success, #10b981)', display: 'flex', alignItems: 'center', gap: 6 }}>{note.bad && <Icon name="warn" size={13} />}{note.text}</p>}
          </Section>

          {t.typed > 0 && (
            <Section title={<span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Icon name="owl" size={15} /> The Owl’s summary</span>}>
              {data.summary?.text
                ? <>
                    <p style={{ fontSize: 13.5, lineHeight: 1.5, margin: '0 0 6px', whiteSpace: 'pre-wrap' }}>{data.summary.text}</p>
                    <p style={small}>Written {ago(data.summary.created_at)} from {data.summary.chats} questions.</p>
                  </>
                : <p style={{ ...small, marginTop: 0 }}>A few sentences on the biggest themes, what’s rising and what fans couldn’t get answered.</p>}
              {data.aiReady && <button type="button" style={btn} disabled={!!busy} onClick={() => post('summary', 'summary')}>{busy === 'summary' ? 'Writing…' : data.summary?.text ? 'Rewrite with the latest' : 'Summarise'}</button>}
            </Section>
          )}

          {t.tagged === 0 && <p style={small}>No chats read yet in this period.</p>}

          {themes.length > 0 && (
            <Section title={`Themes · ${themes.length}`} sub="What the questions were about, biggest first. Tap a theme for the fans’ questions and the chats behind it.">
              <div>
                {shownThemes.map((th, i) => <ThemeRow key={th.theme} th={th} max={max} first={i === 0} trBase={data.transcripts ? trBase : ''} open={open === th.theme} onToggle={() => setOpen(open === th.theme ? '' : th.theme)} />)}
              </div>
              {themes.length > 6 && <button type="button" style={{ ...btn, marginTop: 8 }} onClick={() => setShowAllThemes((v) => !v)}>{showAllThemes ? 'Show the top 6' : `Show all ${themes.length} themes`}</button>}
            </Section>
          )}

          {data.unanswered.length > 0 && (
            <Section title={`Needs an answer · ${t.unanswered}`} sub="Questions the Owl couldn’t answer. Add an FAQ for these (Fan Owl → FAQs) and the Owl answers them from the next chat.">
              <div style={{ display: 'grid', gap: 6 }}>
                {shownGaps.map((u) => <ChatLine key={`${u.sessionId}:${u.day}`} c={{ ...u, answered: 'no' }} sub={u.theme} trBase={data.transcripts ? trBase : ''} />)}
              </div>
              {data.unanswered.length > 5 && <button type="button" style={{ ...btn, marginTop: 8 }} onClick={() => setShowAllGaps((v) => !v)}>{showAllGaps ? 'Show fewer' : `Show all ${data.unanswered.length}`}</button>}
            </Section>
          )}

          {data.starters.length > 0 && (
            <Section title="Starter buttons tapped" sub="Taps on the chat’s suggestion buttons — counted here, never as questions.">
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {data.starters.map((s) => <span key={s.text} style={{ fontSize: 12, border: '1px solid var(--hairline)', borderRadius: 999, padding: '4px 10px', wordBreak: 'break-word' }}>{s.text} · <b>{s.c}</b></span>)}
              </div>
            </Section>
          )}
        </div>
      )}
    </div>
  );
}

// A titled card — each part of the panel is its own section.
function Section({ title, sub = '', children }) {
  return (
    <section style={{ border: '1px solid var(--hairline)', borderRadius: 12, padding: '12px 14px' }}>
      <div style={{ fontSize: 13, fontWeight: 700, marginBottom: sub ? 2 : 8 }}>{title}</div>
      {sub && <p style={{ ...small, margin: '0 0 8px' }}>{sub}</p>}
      {children}
    </section>
  );
}

// One headline number, styled like the Fan Owl report's tiles.
function Stat({ label, value, hint = '', delta = null, tone = '' }) {
  return (
    <div style={{ border: '1px solid var(--hairline)', borderRadius: 10, padding: '8px 12px' }}>
      <div style={{ fontSize: 18, fontWeight: 700, color: tone === 'warn' ? 'var(--warn, #b45309)' : 'inherit' }}>{value ?? 0}</div>
      <div style={{ fontSize: 11, color: 'var(--muted)' }}>{label}</div>
      {delta ? <div style={{ fontSize: 10.5, color: 'var(--muted)', marginTop: 2 }}>{delta > 0 ? '▲' : '▼'} {Math.abs(delta)} vs last period</div>
        : hint ? <div style={{ fontSize: 10.5, color: 'var(--muted)', marginTop: 2 }}>{hint}</div> : null}
    </div>
  );
}

// One theme: name, count and share, the change on the previous period and how
// many went unanswered, over a thin bar; tapped, the fans' questions and chats.
function ThemeRow({ th, max, first, trBase, open, onToggle }) {
  const delta = th.prev == null ? null : th.c - th.prev;
  const gap = th.unanswered + th.partly;
  return (
    <div style={{ borderTop: first ? 0 : '1px solid var(--hairline)' }}>
      <button type="button" onClick={onToggle} aria-expanded={open}
        style={{ width: '100%', textAlign: 'left', font: 'inherit', color: 'inherit', background: 'transparent', border: 0, padding: '10px 0', cursor: 'pointer', minHeight: 44 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '4px 10px', fontSize: 13 }}>
          <b style={{ wordBreak: 'break-word' }}>{th.theme}</b>
          <span style={{ color: 'var(--muted)' }}>{th.c} · {th.share}%</span>
          {delta ? <span style={{ fontSize: 11, color: 'var(--muted)' }}>{delta > 0 ? '▲' : '▼'} {Math.abs(delta)}</span> : null}
          <span style={{ marginLeft: 'auto', fontSize: 11.5, color: gap ? 'var(--warn, #b45309)' : 'var(--muted)' }}>{gap ? `${gap} need${gap === 1 ? 's' : ''} an answer` : 'all answered'}{open ? ' ▴' : ' ▾'}</span>
        </div>
        <div aria-hidden style={{ height: 4, borderRadius: 2, background: 'var(--hairline)', marginTop: 6, overflow: 'hidden' }}>
          <div style={{ width: `${Math.max(3, Math.round((th.c / max) * 100))}%`, height: '100%', background: 'var(--brand, var(--text))', borderRadius: 2 }} />
        </div>
      </button>
      {open && (
        <div style={{ padding: '0 0 12px', display: 'grid', gap: 6 }}>
          {th.examples.map((q) => <div key={q} style={{ fontSize: 12.5, borderLeft: '3px solid var(--hairline)', paddingLeft: 8 }}>“{q}”</div>)}
          <p style={{ ...small, marginTop: 4 }}>
            {th.buying ? `${th.buying} ready to buy · ` : ''}{th.support ? `${th.support} had a problem · ` : ''}{th.frustrated ? `${th.frustrated} frustrated · ` : ''}
            {trBase ? `The ${Math.min(th.c, th.chats.length)} latest chats — tap one to read it:` : 'Latest chats:'}
          </p>
          {th.chats.map((c) => <ChatLine key={`${c.sessionId}:${c.day}`} c={c} trBase={trBase} />)}
        </div>
      )}
    </div>
  );
}

// One chat in a theme or the unanswered list; tap to read the transcript in place
// (only where transcripts are on — the route is the real boundary).
function ChatLine({ c, sub = '', trBase }) {
  const [open, setOpen] = useState(false);
  const [t, setT] = useState(null);
  useEffect(() => {
    if (!open || t || !trBase) return undefined;
    let on = true;
    fetch(`${trBase}/${encodeURIComponent(c.sessionId)}?day=${c.day}`).then((r) => r.json())
      .then((d) => { if (on) setT(d); }).catch(() => { if (on) setT({ messages: [] }); });
    return () => { on = false; };
  }, [open, t, trBase, c.sessionId, c.day]);
  const when = c.lastAt ? new Date(c.lastAt).toLocaleString([], { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';
  return (
    <div style={{ border: '1px solid var(--hairline)', borderRadius: 8 }}>
      <button type="button" disabled={!trBase} onClick={() => setOpen((o) => !o)} aria-expanded={open}
        style={{ width: '100%', textAlign: 'left', font: 'inherit', color: 'inherit', background: 'transparent', border: 0, padding: '8px 10px', cursor: trBase ? 'pointer' : 'default', minHeight: 44 }}>
        <div style={{ fontSize: 12.5, wordBreak: 'break-word' }}>{c.question || '—'}</div>
        <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
          <span style={{ display: 'inline-block', verticalAlign: '-2px', marginRight: 6 }}><Icon name={c.channel === 'whatsapp' ? 'chat' : 'web'} size={12} /></span>
          {when}{sub ? ` · ${sub}` : ''} · {ANSWER_LABEL[c.answered] || ''}
        </div>
      </button>
      {open && (
        <div style={{ borderTop: '1px solid var(--hairline)', padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 6 }}>
          {!t && <p style={small}>Loading the chat…</p>}
          {t && (t.messages || []).map((m, i) => (
            <div key={i} style={{ alignSelf: m.from === 'fan' ? 'flex-end' : 'flex-start', maxWidth: '88%' }}>
              <div style={{ fontSize: 12.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word', padding: '7px 10px', borderRadius: 12, border: '1px solid var(--hairline)', background: m.from === 'fan' ? 'var(--surface-2, rgba(0,0,0,0.04))' : 'transparent' }}>{m.body}</div>
              <div style={{ fontSize: 10.5, color: 'var(--muted)', marginTop: 2, textAlign: m.from === 'fan' ? 'right' : 'left' }}>{m.from === 'fan' ? 'Fan' : 'Owl'}</div>
            </div>
          ))}
          {t && !(t.messages || []).length && <p style={small}>No messages to show.</p>}
        </div>
      )}
    </div>
  );
}
